import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import Logo from "@/components/landing/Logo";

const Login = () => {
  const { user, login, register, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", username: "", displayName: "" });
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (twofa) {
        await verify2fa(twofa.challengeId, code);
      } else if (mode === "login") {
        const r = await login(form.email, form.password);
        if (r?.twoFactorRequired) setTwofa(r);
      } else {
        await register({
          email: form.email,
          password: form.password,
          username: form.username,
          displayName: form.displayName || undefined,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setError("");
    setNotice("");
    if (!form.email) return setError("Enter your email address first.");
    try {
      await api.post("/auth/request-reset", { email: form.email });
      setNotice("If that email is registered, a reset link is on its way.");
    } catch (err) {
      setError(err.message);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors focus:border-crimson/60";

  return (
    <div className="relative flex min-h-[calc(100dvh-10rem)] items-center justify-center py-8" data-testid="web-login-page">
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-surface/80 p-8 backdrop-blur-xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size={56} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-white">Michael C Streamline</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in with the same account you use on your phone.</p>
        </div>

        {twofa ? (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-400">
              Enter the 6-digit code we sent to <span className="text-white">{twofa.emailHint || "your email"}</span>.
            </p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              data-testid="twofa-code-input"
              className={`${inputCls} text-center font-mono text-lg tracking-[0.4em]`}
            />
            {error && <p className="text-sm text-crimson" data-testid="login-error">{error}</p>}
            <button disabled={busy} data-testid="twofa-submit-button" className="w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
              {busy ? "Verifying…" : "Verify & sign in"}
            </button>
          </form>
        ) : (
          <>
            <div className="mb-6 grid grid-cols-2 rounded-full border border-white/10 bg-white/[0.03] p-1">
              {[["login", "Sign in"], ["register", "Create account"]].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(""); }}
                  data-testid={`auth-tab-${m}`}
                  className={`rounded-full py-2 font-heading text-sm font-semibold transition-colors ${mode === m ? "bg-crimson text-white" : "text-slate-400 hover:text-white"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <>
                  <input value={form.username} onChange={set("username")} placeholder="Username" required data-testid="register-username-input" className={inputCls} />
                  <input value={form.displayName} onChange={set("displayName")} placeholder="Display name (optional)" data-testid="register-displayname-input" className={inputCls} />
                </>
              )}
              <input type="email" value={form.email} onChange={set("email")} placeholder="Email address" required data-testid="login-email-input" className={inputCls} />
              <input type="password" value={form.password} onChange={set("password")} placeholder="Password" required data-testid="login-password-input" className={inputCls} />
              {error && <p className="text-sm text-crimson" data-testid="login-error">{error}</p>}
              {notice && <p className="text-sm text-emerald-400" data-testid="login-notice">{notice}</p>}
              <button disabled={busy} data-testid="login-submit-button" className="w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>
            {mode === "login" && (
              <button onClick={forgot} className="mt-4 w-full text-center text-xs text-slate-500 transition-colors hover:text-white" data-testid="forgot-password-link">
                Forgot your password?
              </button>
            )}
          </>
        )}

        <Link to="/about" className="mt-6 block text-center text-xs text-slate-600 transition-colors hover:text-slate-300" data-testid="back-to-website-link">
          ← About the app
        </Link>
      </div>
    </div>
  );
};

export default Login;
